import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CorporateGroup, {
	CorporateGroupMember,
	CreateCorporateGroupRequest,
	UpdateCorporateGroupRequest,
} from "../models/CorporateGroup";
import { apiStore } from "../stores/api.store";

export type GetCorporateGroupsParams = {
	page?: number;
	pageSize?: number;
	keyword?: string;
	sort?: string;
	order?: string;
};

export function useCorporateGroups(query: GetCorporateGroupsParams, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["corporate-groups", query],
		() =>
			apiStore
				.get<{ totalCount: number; corporateGroups: CorporateGroup[] }>({
					path: "/corporate-group",
					isAuthRequired: true,
					query,
				})
				.then((d) => d.data),
		{
			keepPreviousData: true,
			enabled,
		}
	);

	return {
		corporateGroups: data,
		...rest,
	};
}

export function useCorporateGroup(corporateGroupId?: string, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["corporate-groups", corporateGroupId],
		() =>
			apiStore
				.get<CorporateGroup & { members: CorporateGroupMember[] }>({
					path: `/corporate-group/${corporateGroupId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			enabled: enabled !== false && !!corporateGroupId,
		}
	);

	return {
		corporateGroup: data,
		...rest,
	};
}

export function useCreateCorporateGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		(body: CreateCorporateGroupRequest) =>
			apiStore
				.post<CorporateGroup>({
					path: "/corporate-group",
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["corporate-groups"]);
			},
		}
	);
}

export function useUpdateCorporateGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		(params: { corporateGroupId: string; update: UpdateCorporateGroupRequest }) =>
			apiStore
				.put<CorporateGroup>({
					path: `/corporate-group/${params.corporateGroupId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["corporate-groups"]);
				// Member customers' resolved invoice details may have changed
				queryClient.invalidateQueries(["customers"]);
			},
		}
	);
}

export function useDeleteCorporateGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		(corporateGroupId: string) =>
			apiStore
				.delete({
					path: `/corporate-group/${corporateGroupId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["corporate-groups"]);
			},
		}
	);
}
